import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Stack,
  TextField,
  Paper,
  Fade,
  Zoom,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import html2canvas from "html2canvas";
import backgroundDiamondHunt from "@/assets/backgroundDiamondHuntLaptop.png";
import backgroundDiamondHuntTablet from "@/assets/backgroundDiamondHuntTablet.png";
import backgroundDiamondHuntMobile from "@/assets/backgroundDiamondHuntPhone.png";
import storage from "@/utils/storage";
import WinModal from "./WinModaHuntl";

// Styled components for better responsiveness
const HuntEventContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
//   backgroundImage: `url(${backgroundDiamondHunt})`,
  backgroundSize: "100%",
  position: "relative",
  display: "flex",
  justifyContent: "center",
  
  // Tablet responsiveness
  [theme.breakpoints.down('lg')]: {
    backgroundImage: `url(${backgroundDiamondHunt})`,
    backgroundPosition: "center top",
    paddingTop: theme.spacing(4),
  },
  
  // Mobile responsiveness
  [theme.breakpoints.down('md')]: {
     backgroundImage: `url(${backgroundDiamondHuntTablet})`, 
     padding: theme.spacing(2, 1),
     paddingTop: theme.spacing(3),
     backgroundPosition: "center top",
  },
  [theme.breakpoints.down('sm')]: {
    backgroundImage: `url(${backgroundDiamondHuntMobile})`, // Keep mobile background
    backgroundSize: "cover",
    backgroundPosition: "center center",
    padding: theme.spacing(1, 0.5),
    paddingTop: theme.spacing(2),
  },
  
  // Overlay for better text readability
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)", // Reduced opacity for better visibility
    zIndex: 1,
  },
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  maxWidth: "1200px",
  margin: "0 auto",
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1),
  },
}));

const HuntCard = styled(Card)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  padding: theme.spacing(3),
  textAlign: "center",
  
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    margin: theme.spacing(1, 0),
  },
}));

const HuntTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Ubuntu, sans-serif",
  fontWeight: 900,
  background: "linear-gradient(45deg, #FFD700 0%, #FF6B35 50%, #8A2BE2 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  marginBottom: theme.spacing(2),
  textShadow: "0 0 30px rgba(255, 215, 0, 0.8)",
  position: "relative",
  animation: "titleGlow 3s ease-in-out infinite alternate",
  textAlign: "center",
  
  "@keyframes titleGlow": {
    "0%": {
      filter: "brightness(1) drop-shadow(0 0 10px rgba(255,215,0,0.6))",
      transform: "scale(1)",
    },
    "100%": {
      filter: "brightness(1.3) drop-shadow(0 0 25px rgba(255,215,0,1))",
      transform: "scale(1.02)",
    },
  },
  
  // Large screen enhancements
  [theme.breakpoints.up('xl')]: {
    fontSize: "4.5rem !important",
    fontWeight: 900,
    letterSpacing: "4px",
    lineHeight: 1.1,
    textShadow: "0 0 50px rgba(255, 215, 0, 1)",
    marginBottom: theme.spacing(4),
  },
  
  [theme.breakpoints.up('lg')]: {
    fontSize: "3.5rem !important",
    fontWeight: 900,
    letterSpacing: "3px",
    lineHeight: 1.2,
    textShadow: "0 0 40px rgba(255, 215, 0, 0.9)",
    marginBottom: theme.spacing(3),
  },
  
  [theme.breakpoints.down('md')]: {
    fontSize: "2.5rem",
    letterSpacing: "2px",
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: "2rem",
    letterSpacing: "1px",
  },
  
  [theme.breakpoints.down('xs')]: {
    fontSize: "1.5rem",
    letterSpacing: "0.5px",
  },
}));

const HuntButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #FF1744 0%, #FF6B35 50%, #FFD700 100%)",
  border: "3px solid rgba(255, 255, 255, 0.3)",
  borderRadius: 35,
  boxShadow: `
    0 8px 30px rgba(255, 23, 68, 0.5), 
    inset 0 2px 0 rgba(255,255,255,0.4),
    inset 0 -2px 0 rgba(0,0,0,0.2)
  `,
  color: "white",
  height: 64,
  padding: "0 50px",
  fontSize: "1.2rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "2px",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  
  // Enhanced 3D button effect
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "0",
    height: "0",
    background: "radial-gradient(circle, rgba(255,255,255,0.8), transparent)",
    transition: "all 0.6s ease",
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
  },
  
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
    transition: "left 0.6s",
    zIndex: 1,
  },
  
  "&:hover": {
    transform: "translateY(-4px) scale(1.05)",
    boxShadow: `
      0 12px 40px rgba(255, 23, 68, 0.7), 
      0 0 35px rgba(255,107,53,0.5),
      inset 0 3px 0 rgba(255,255,255,0.5),
      inset 0 -3px 0 rgba(0,0,0,0.3)
    `,
    background: "linear-gradient(45deg, #FF6B35 0%, #FF1744 50%, #8A2BE2 100%)",
    border: "3px solid rgba(255, 255, 255, 0.5)",
    
    "&::before": {
      left: "100%",
    },
    
    "&::after": {
      width: "300px",
      height: "300px",
      opacity: 0,
    },
  },
  
  "&:active": {
    transform: "translateY(-2px) scale(1.02)",
    boxShadow: `
      0 6px 20px rgba(255, 23, 68, 0.8),
      inset 0 1px 0 rgba(255,255,255,0.3),
      inset 0 -1px 0 rgba(0,0,0,0.2)
    `,
  },
  
  "&:disabled": {
    background: "linear-gradient(45deg, #444 0%, #777 100%)",
    border: "3px solid rgba(255, 255, 255, 0.2)",
    transform: "none",
    boxShadow: `
      inset 0 2px 0 rgba(0,0,0,0.3),
      inset 0 -2px 0 rgba(255,255,255,0.1)
    `,
    opacity: 0.7,
    cursor: "not-allowed",
    color: "#aaa",
    
    "&:hover": {
      transform: "none",
      boxShadow: `
        inset 0 2px 0 rgba(0,0,0,0.3),
        inset 0 -2px 0 rgba(255,255,255,0.1)
      `,
      background: "linear-gradient(45deg, #444 0%, #777 100%)",
    },
  },
  
  // Large screen enhancements
  [theme.breakpoints.up('lg')]: {
    height: 72,
    padding: "0 60px",
    fontSize: "1.4rem",
    borderRadius: 40,
    letterSpacing: "3px",
    fontWeight: 900,
  },
  
  [theme.breakpoints.down('sm')]: {
    height: 52,
    padding: "0 30px",
    fontSize: "1rem",
    letterSpacing: "1px",
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(5px)",
  borderRadius: theme.spacing(1.5),
  height: "100%",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
  },
  
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(1, 0),
  },
}));

const TransparentTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(15px)",
    border: "4px solid transparent",
    borderRadius: "25px",
    color: "#fff",
    fontSize: "1.3rem",
    fontWeight: 800,
    textAlign: "center",
    textTransform: "uppercase",
    letterSpacing: "4px",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
 
    background: `
      linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)) padding-box, 
      linear-gradient(45deg, #FF1744, #FFD700, #8A2BE2, #40E0D0) border-box
    `,
    boxShadow: `
      0 8px 25px rgba(0, 0, 0, 0.3),
      inset 0 2px 0 rgba(255,255,255,0.1),
      inset 0 -2px 0 rgba(0,0,0,0.2)
    `,
    
    "& fieldset": {
      border: "none",
    },
    
    // Enhanced input field definition
    "&::after": {
      content: '""',
      position: "absolute",
      top: "3px",
      left: "3px",
      right: "3px",
      bottom: "3px",
      background: "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
      borderRadius: "20px",
      pointerEvents: "none",
      zIndex: 1,
    },
    
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255,215,0,0.4), transparent)",
      transition: "left 0.8s",
        
      zIndex: 2,
    },
    
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      transform: "scale(1.02)",
      boxShadow: `
        0 12px 35px rgba(255, 215, 0, 0.4), 
        inset 0 0 25px rgba(255,107,53,0.1),
        inset 0 3px 0 rgba(255,255,255,0.15),
        inset 0 -3px 0 rgba(0,0,0,0.25)
      `,
      background: `
        linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)) padding-box, 
        linear-gradient(45deg, #FFD700, #FF1744, #40E0D0, #8A2BE2) border-box
      `,
      
      "&::before": {
        left: "100%",
      },
    },
    
    "&.Mui-focused": {
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      transform: "scale(1.03)",
      boxShadow: `
        0 15px 50px rgba(255, 23, 68, 0.6), 
        0 0 30px rgba(255,215,0,0.8), 
        inset 0 0 35px rgba(138,43,226,0.15),
        inset 0 4px 0 rgba(255,255,255,0.2),
        inset 0 -4px 0 rgba(0,0,0,0.3)
      `,
      background: `
        linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)) padding-box, 
        linear-gradient(45deg, #FF1744, #FFD700, #40E0D0, #8A2BE2) border-box
      `,
      animation: "focusPulse 2s infinite",
    },
    
    "@keyframes focusPulse": {
      "0%": {
        boxShadow: `
          0 15px 50px rgba(255, 23, 68, 0.6), 
          0 0 30px rgba(255,215,0,0.8), 
          inset 0 0 35px rgba(138,43,226,0.15)
        `,
      },
      "50%": {
        boxShadow: `
          0 20px 60px rgba(255, 23, 68, 0.8), 
          0 0 40px rgba(255,215,0,1), 
          inset 0 0 45px rgba(138,43,226,0.25)
        `,
      },
      "100%": {
        boxShadow: `
          0 15px 50px rgba(255, 23, 68, 0.6), 
          0 0 30px rgba(255,215,0,0.8), 
          inset 0 0 35px rgba(138,43,226,0.15)
        `,
      },
    },
    
    "& input": {
      textAlign: "center",
      fontSize: "1.4rem",
      fontWeight: 800,
      color: "#fff",
      textShadow: "0 0 15px rgba(255,215,0,0.9), 3px 3px 6px rgba(0,0,0,0.8)",
      position: "relative",
      zIndex: 3,
      padding: "18px 20px",
      
      "&::placeholder": {
        color: "rgba(255, 215, 0, 0.8)",
        opacity: 1,
        fontWeight: 700,
        textShadow: "2px 2px 4px rgba(0,0,0,0.8)",
        letterSpacing: "3px",
      },
    },
  },
  
  "& .MuiFormHelperText-root": {
    color: "#FFFFFF",
    fontWeight: 900,
    fontSize: "1rem",
    fontFamily: "Ubuntu, sans-serif",
    textShadow: `
      0 0 10px rgba(255,255,255,0.8),
      2px 2px 4px rgba(0,0,0,1),
      0 0 20px rgba(255,23,68,0.6)
    `,
    textAlign: "center",
    marginTop: theme.spacing(1),
    animation: "errorPulse 1.5s ease-in-out infinite",
    background: `
      linear-gradient(135deg, 
        rgba(255, 23, 68, 0.95) 0%, 
        rgba(255, 107, 53, 0.9) 50%,
        rgba(138, 43, 226, 0.85) 100%
      )
    `,
    borderRadius: "16px",
    padding: "14px 24px",
    border: "2px solid rgba(255, 255, 255, 0.8)",
    boxShadow: `
      0 8px 25px rgba(255, 23, 68, 0.4),
      0 0 40px rgba(255, 107, 53, 0.3),
      inset 0 2px 4px rgba(255,255,255,0.2),
      inset 0 -2px 4px rgba(0,0,0,0.3)
    `,
    backdropFilter: "blur(15px)",
    position: "relative",
    overflow: "hidden",
    
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
      animation: "shimmer 2s infinite",
      zIndex: 1,
    },
    
    "&::after": {
      content: '""',
      position: "absolute",
      top: "2px",
      left: "2px",
      right: "2px",
      bottom: "2px",
      background: "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
      borderRadius: "14px",
      pointerEvents: "none",
      zIndex: 0,
    },
  },
  
  "@keyframes errorPulse": {
    "0%": { 
      transform: "scale(1)",
      boxShadow: `
        0 8px 25px rgba(255, 23, 68, 0.4),
        0 0 40px rgba(255, 107, 53, 0.3)
      `
    },
    "50%": { 
      transform: "scale(1.02)",
      boxShadow: `
        0 12px 35px rgba(255, 23, 68, 0.6),
        0 0 60px rgba(255, 107, 53, 0.5)
      `
    },
    "100%": { 
      transform: "scale(1)",
      boxShadow: `
        0 8px 25px rgba(255, 23, 68, 0.4),
        0 0 40px rgba(255, 107, 53, 0.3)
      `
    },
  },
  
  "@keyframes shimmer": {
    "0%": { left: "-100%" },
    "100%": { left: "100%" },
  },
  
  "@keyframes errorShake": {
    "0%": { transform: "translateX(0)" },
    "25%": { transform: "translateX(-8px)" },
    "50%": { transform: "translateX(8px)" },
    "75%": { transform: "translateX(-8px)" },
    "100%": { transform: "translateX(0)" },
  },
  
  // Large screen enhancements
  [theme.breakpoints.up('lg')]: {
    "& .MuiOutlinedInput-root": {
      fontSize: "1.5rem",
      letterSpacing: "5px",
      borderRadius: "30px",
      border: "5px solid transparent",
      
      "& input": {
        fontSize: "1.6rem",
        padding: "22px 25px",
        letterSpacing: "5px",
        
        "&::placeholder": {
          letterSpacing: "4px",
        },
      },
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    "& .MuiOutlinedInput-root": {
      fontSize: "1.1rem",
      letterSpacing: "2px",
      borderRadius: "20px",
      
      "& input": {
        fontSize: "1.2rem",
        padding: "14px 16px",
        letterSpacing: "2px",
      },
    },
  },
}));

const HuntSection = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(138,43,226,0.3) 50%, rgba(0,0,0,0.8) 100%)",
  backdropFilter: "blur(20px)",
  borderRadius: theme.spacing(4),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: "2px solid transparent",
  marginTop:"77px",
//   backgroundImage: `
//     linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)),
//     linear-gradient(45deg, #FF1744, #FFD700, #8A2BE2, #FF6B35)
//   `,
  backgroundOrigin: "border-box",
  backgroundClip: "padding-box, border-box",
  boxShadow: `
    0 20px 60px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255,255,255,0.1),
    0 0 50px rgba(255, 23, 68, 0.3)
  `,
  position: "relative",
  overflow: "hidden",
  
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    zIndex: 0,
  },
  
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle at 50% 50%, rgba(255,215,0,0.05) 0%, transparent 70%)",
    zIndex: 1,
  },
  
  "@keyframes rotate": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  
  "& > *": {
    position: "relative",
    zIndex: 2,
  },
  
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    margin: theme.spacing(1.5, 0),
  },
}));

const HuntEvent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Animation states
  const [showContent, setShowContent] = useState(false);
  const [pulseEffect, setPulseEffect] = useState(false);

  // State for Golden Ticket Hunt
  const [goldenTicketCode, setGoldenTicketCode] = useState('');
  const [goldenTicketError, setGoldenTicketError] = useState('');
  const [goldenTicketLoading, setGoldenTicketLoading] = useState(false);

  // State for Diamond Ticket Hunt (5 codes)
  const [diamondTicketCodes, setDiamondTicketCodes] = useState(['', '', '', '', '']);
  const [diamondTicketErrors, setDiamondTicketErrors] = useState(['', '', '', '', '']);
  const [diamondTicketLoading, setDiamondTicketLoading] = useState(false);
  const [diamondCodeValidation, setDiamondCodeValidation] = useState(['', '', '', '', '']); // 'valid', 'invalid', or ''
  const [validatingCode, setValidatingCode] = useState([false, false, false, false, false]); // Loading states for each code

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Win Modal states
  const [winModal, setWinModal] = useState({
    open: false, // Changed to false so modal doesn't show by default
    ticketType: 'golden' as 'golden' | 'diamond',
    ticketCode: '',
    winMessage: '',
    prizeAmount: 0,
    hasWin: true // Set to true for proper modal display
  });

  // Screenshot function
  const takeScreenshot = async () => {
    try {
      // Wait a bit for the modal to fully render
      setTimeout(async () => {
        // Find the modal backdrop and content
        const modalBackdrop = document.querySelector('[data-modal-content="true"]')?.parentElement;
        const modalContent = document.querySelector('[data-modal-content="true"]');
        
        if (!modalBackdrop || !modalContent) {
          throw new Error('Modal not found');
        }

        // Create a temporary container that matches the original modal structure
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'fixed';
        tempContainer.style.top = '0';
        tempContainer.style.left = '0';
        tempContainer.style.width = '100vw';
        tempContainer.style.height = '100vh';
        tempContainer.style.display = 'flex';
        tempContainer.style.justifyContent = 'center';
        tempContainer.style.alignItems = 'center';
        // Copy the original modal backdrop styling - dark with blur
        tempContainer.style.background = 'rgba(0, 0, 0, 0.95)';
        tempContainer.style.backdropFilter = 'blur(8px)';
        tempContainer.style.zIndex = '10000';
        tempContainer.style.padding = '0.5rem';
        
        // Clone the modal content with its original styling
        const modalClone = modalContent.cloneNode(true) as HTMLElement;
        // Preserve the original styling but remove animations for clean screenshot
        modalClone.style.position = 'relative';
        modalClone.style.animation = 'none';
        modalClone.style.transform = 'none';
        // Keep the original background gradient and border styling
        modalClone.style.background = 'linear-gradient(145deg, rgba(5, 5, 24, 0.98), rgba(10, 10, 40, 0.98))';
        modalClone.style.backgroundImage = `
          linear-gradient(145deg, rgba(5, 5, 24, 0.98), rgba(10, 10, 40, 0.98)),
          linear-gradient(45deg, #FFD700, #FF6B35, #8A2BE2, #40E0D0)
        `;
        modalClone.style.backgroundOrigin = 'border-box';
        modalClone.style.backgroundClip = 'padding-box, border-box';
        modalClone.style.border = '3px solid transparent';
        modalClone.style.borderRadius = '24px';
        modalClone.style.boxShadow = `
          0 25px 80px rgba(0, 0, 0, 0.8),
          0 0 60px rgba(255, 215, 0, 0.2),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `;
        
        tempContainer.appendChild(modalClone);
        document.body.appendChild(tempContainer);

        // Take screenshot of the temporary container
        const canvas = await html2canvas(tempContainer, {
          allowTaint: true,
          useCORS: true,
          scale: 2, // Higher quality
          backgroundColor: 'rgba(0, 0, 0, 0.95)', // Match modal backdrop
          logging: false,
          width: window.innerWidth,
          height: window.innerHeight,
          onclone: (clonedDoc) => {
            // Keep the original styling but ensure no interfering animations
            const clonedElements = clonedDoc.querySelectorAll('*');
            clonedElements.forEach(el => {
              const element = el as HTMLElement;
              // Remove animations but keep other styling
              element.style.animation = 'none';
              element.style.animationDelay = '0s';
              element.style.animationDuration = '0s';
            });
          }
        });

        // Clean up temporary container
        document.body.removeChild(tempContainer);

        // Create download link
        const link = document.createElement('a');
        link.download = `hunt-event-${winModal.ticketType}-win-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0); // Highest quality
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show notification
        setNotification({
          open: true,
          message: '🎉 Screenshot saved! Check your downloads folder.',
          severity: 'success'
        });
      }, 1500); // Increased delay for better modal render
    } catch (error) {
      console.error('Screenshot failed:', error);
      setNotification({
        open: true,
        message: '❌ Failed to take screenshot. Please try again.',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    setShowContent(true);
    const interval = setInterval(() => {
      setPulseEffect(prev => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Validation function for alphanumeric codes
  const validateCode = (code: string) => {
    const alphanumericRegex = /^[A-Z0-9]+$/;
    if (!code) return 'Code is required';
    // if (code.length !== 12) return 'Code must be exactly 12 characters';
    if (!alphanumericRegex.test(code)) return 'Code must contain only letters and numbers';
    return '';
  };

  // Validate individual diamond code via API
  const validateDiamondCode = async (code: string, index: number) => {
    if (!code || code.length < 3) {
      // Reset validation state for short codes
      const newValidation = [...diamondCodeValidation];
      newValidation[index] = '';
      setDiamondCodeValidation(newValidation);
      return;
    }

    // Set loading state for this specific code
    const newValidatingState = [...validatingCode];
    newValidatingState[index] = true;
    setValidatingCode(newValidatingState);

    try {
      let token = storage.getToken();
      const response = await axios.post(`${process.env.API_URL_PREFIX}/api/v1/user/validate-diamond-code`, {
        codes: code,
        sequence: index + 1 // Send 1-based sequence (1, 2, 3, 4, 5)
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Access-Control-Allow-Origin": "*",
        },
      });

      // Check if the response indicates success or failure
      const responseData = response.data;
      const newValidation = [...diamondCodeValidation];
      const newErrors = [...diamondTicketErrors];

      if (responseData.data && responseData.data.success === false) {
        // Mark as invalid based on API response
        newValidation[index] = 'invalid';
        newErrors[index] = responseData.data.message || '❌ Invalid code';
      } else {
        // Mark as valid
        newValidation[index] = 'valid';
        newErrors[index] = '';
      }

      setDiamondCodeValidation(newValidation);
      setDiamondTicketErrors(newErrors);
      
    } catch (error: any) {
      // Mark as invalid for network/server errors
      const newValidation = [...diamondCodeValidation];
      newValidation[index] = 'invalid';
      setDiamondCodeValidation(newValidation);

      // Set error message
      const newErrors = [...diamondTicketErrors];
      newErrors[index] = error.response?.data?.message || error.response?.data?.error || 'Network error - please try again';
      setDiamondTicketErrors(newErrors);
      
      console.error('Diamond Code Validation Error:', error);
    } finally {
      // Remove loading state for this code
      const newValidatingState = [...validatingCode];
      newValidatingState[index] = false;
      setValidatingCode(newValidatingState);
    }
  };

  // Handle Golden Ticket input
  const handleGoldenTicketChange = (value: string) => {
    const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (upperValue.length <= 12) {
      setGoldenTicketCode(upperValue);
      setGoldenTicketError(validateCode(upperValue));
    }
  };

  // Handle Diamond Ticket input
  const handleDiamondTicketChange = (index: number, value: string) => {
    const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (upperValue.length <= 50) {
      const newCodes = [...diamondTicketCodes];
      newCodes[index] = upperValue;
      setDiamondTicketCodes(newCodes);
      
      const newErrors = [...diamondTicketErrors];
      newErrors[index] = validateCode(upperValue);
      setDiamondTicketErrors(newErrors);

      // Reset validation state when code changes
      const newValidation = [...diamondCodeValidation];
      newValidation[index] = '';
      setDiamondCodeValidation(newValidation);

      // Validate code if it's long enough and has no basic validation errors
      if (upperValue.length >= 3 && !validateCode(upperValue)) {
        // Debounce the API call
        const timeoutId = setTimeout(() => {
          validateDiamondCode(upperValue, index);
        }, 500); // 500ms delay after user stops typing

        // Store timeout ID to clear previous timeouts
        return () => clearTimeout(timeoutId);
      }
    }
  };

  // Submit handlers
  const handleGoldenTicketSubmit = async () => {
    const error = validateCode(goldenTicketCode);
    if (error) {
      setGoldenTicketError(error);
      return;
    }

    setGoldenTicketLoading(true);
    setGoldenTicketError('');

    try {
         let token = storage.getToken();
      const response = await axios.post(`${process.env.API_URL_PREFIX}/api/v1/user/match-golden-ticket`, {
        ticketNumber: goldenTicketCode
      },
       {
          headers: {
            Authorization: `Bearer ${token}`,
            "Access-Control-Allow-Origin": "*",
          },
        }
    );

      // Show Win Modal for success
      setWinModal({
        open: true,
        ticketType: 'golden',
        ticketCode: goldenTicketCode,
        winMessage: response.data.message || 'Golden ticket matched successfully! 🏆',
        prizeAmount: response.data.prizeAmount,
        hasWin: true
      });

      // Take screenshot automatically on win
      takeScreenshot();

      // Reset the form
      setGoldenTicketCode('');
      
    } catch (error: any) {
      // Handle error response
      const errorMessage = error.response?.data?.message || 'Failed to match golden ticket. Please try again.';
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      console.error('Golden Ticket Error:', error);
    } finally {
      setGoldenTicketLoading(false);
    }
  };

  const handleDiamondTicketSubmit = async () => {
    const errors = diamondTicketCodes.map(validateCode);
    setDiamondTicketErrors(errors);
    
    if (errors.some(error => error)) {
      return;
    }

    setDiamondTicketLoading(true);

    try {
      let token = storage.getToken();
      const response = await axios.post(`${process.env.API_URL_PREFIX}/api/v1/user/match-diamond-ticket`, {
        codes: diamondTicketCodes
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Access-Control-Allow-Origin": "*",
        },
      });

      // Show Win Modal for success
      setWinModal({
        open: true,
        ticketType: 'diamond',
        ticketCode: diamondTicketCodes.join(', '),
        winMessage: response.data.message || 'Diamond tickets matched successfully! 💎',
        prizeAmount: response.data.prizeAmount,
        hasWin: true
      });

      // Take screenshot automatically on win
      takeScreenshot();

      // Reset the form
      setDiamondTicketCodes(['', '', '', '', '']);
      setDiamondTicketErrors(['', '', '', '', '']);
      
    } catch (error: any) {
    console.log("errorMessage", error);
      const errorMessage = error.response?.data.error;
     
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      console.error('Diamond Ticket Error:', error);
    } finally {
      setDiamondTicketLoading(false);
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Close win modal
  const handleCloseWinModal = () => {
    setWinModal({ ...winModal, open: false });
  };

  return (
    <HuntEventContainer>
      <ContentWrapper>
        <Fade in={showContent} timeout={1000}>
          <Grid container spacing={isMobile ? 2 : 4} justifyContent="center" alignItems="center">
            
            {/* Golden Ticket Hunt Section */}
            <Grid item xs={12}>
              <Zoom in={showContent} timeout={1200}>
                <HuntSection elevation={12}>
                  <HuntTitle variant={isMobile ? "h4" : "h2"} sx={{ 
                    color: "#FFD700", 
                    textAlign: "center",
                    filter: pulseEffect ? "drop-shadow(0 0 20px #FFD700)" : "drop-shadow(0 0 10px #FFD700)"
                  }}>
                 MERV'S DIAMOND TICKET HUNT 
                  </HuntTitle>
                  <Typography 
                    variant={isMobile ? "body1" : "h6"} 
                    sx={{ 
                      fontFamily: "Ubuntu, sans-serif",
                      lineHeight: 1.8,
                      maxWidth: "700px",
                      margin: "0 auto 32px auto",
                      textAlign: "center",
                      color: "#FFD700",
                      fontWeight: 600,
                      textShadow: "0 0 10px rgba(255,215,0,0.5), 2px 2px 4px rgba(0,0,0,0.8)",
                      fontSize: isMobile ? "1rem" : "1.2rem"
                    }}
                  >
                    🎯 Find the code - Claim your Treasure - Become a Legend 🎯
                    <br />
                    <Box component="span" sx={{ fontSize: "0.9em", opacity: 0.9 }}>
                      Enter the mystical 12-character code to claim your Golden Ticket!
                    </Box>
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 3,
                    position: 'relative'
                  }}>
                    <Zoom in={showContent} timeout={1500}>
                      <Box sx={{ width: isMobile ? '100%' : '600px', position: 'relative' }}>
                        <TransparentTextField
                          fullWidth
                          placeholder="🔥 ENTER GOLDEN CODE 🔥"
                          value={goldenTicketCode}
                          onChange={(e) => handleGoldenTicketChange(e.target.value)}
                          error={!!goldenTicketError}
                          helperText={goldenTicketError}
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              height: isMobile ? '48px' : '64px'
                            }
                          }}
                          inputProps={{
                            maxLength: 12,
                            style: { textAlign: 'center' }
                          }}
                        />
                        {/* Floating sparkles effect */}
                        <Box sx={{
                          position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          width: '20px',
                          height: '20px',
                          background: 'radial-gradient(circle, #FFD700, transparent)',
                          borderRadius: '50%',
                          animation: 'float 3s ease-in-out infinite',
                          '@keyframes float': {
                            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                            '50%': { transform: 'translate(10px, -10px) scale(1.2)' },
                          }
                        }} />
                      </Box>
                    </Zoom>
                    
                    <Zoom in={showContent} timeout={1800}>
                      <HuntButton 
                        variant="contained" 
                        size={isMobile ? "large" : "large"}
                        onClick={handleGoldenTicketSubmit}
                        disabled={!!goldenTicketError || !goldenTicketCode || goldenTicketLoading}
                        sx={{
                          background: "linear-gradient(45deg, #FFD700 0%, #FF8C00 50%, #FF6347 100%)",
                          fontSize: isMobile ? "1.1rem" : "1.3rem",
                          padding: isMobile ? "12px 30px" : "16px 50px",
                          position: 'relative',
                          '&:hover': {
                            background: "linear-gradient(45deg, #FF6347 0%, #FFD700 50%, #FF8C00 100%)",
                          }
                        }}
                      >
                        {goldenTicketLoading ? (
                          <>
                            <CircularProgress 
                              size={24} 
                              sx={{ 
                                color: 'white', 
                                marginRight: 1,
                                position: 'absolute',
                                left: '20px'
                              }} 
                            />
                            <Box sx={{ ml: 4 }}>PROCESSING...</Box>
                          </>
                        ) : (
                          '🏆 CLAIM GOLDEN TREASURE 🏆'
                        )}
                      </HuntButton>
                    </Zoom>
                  </Box>
                </HuntSection>
              </Zoom>
            </Grid>

            {/* Diamond Ticket Hunt Section */}
            <Grid item xs={12}>
              <Zoom in={showContent} timeout={1500}>
                <HuntSection elevation={12}>
                  <HuntTitle variant={isMobile ? "h4" : "h2"} sx={{ 
                    color: "#40E0D0", 
                    textAlign: "center",
                    filter: pulseEffect ? "drop-shadow(0 0 20px #40E0D0)" : "drop-shadow(0 0 10px #40E0D0)"
                  }}>
                    DIAMOND TREASURE HUNT
                  </HuntTitle>
                  <Typography 
                    sx={{ 
                      fontFamily: "Ubuntu, sans-serif",
                      lineHeight: 1.8,
                      maxWidth: "700px",
                      margin: "0 auto 32px auto",
                      textAlign: "center",
                      color: "#40E0D0",
                      fontWeight: 600,
                      textShadow: "0 0 10px rgba(64,224,208,0.5), 2px 2px 4px rgba(0,0,0,0.8)",
                      fontSize: isMobile ? "1rem" : "1.2rem"
                    }}
                  >
                    ⚔️ Find the code - Claim your Treasure - Become a Legend ⚔️
                    <br />
                    <Box component="span" sx={{ fontSize: "0.9em", opacity: 0.9 }}>
                      Uncover all 5 diamond codes and unlock the ultimate premium rewards!
                    </Box>
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 4,
                    position: 'relative'
                  }}>
                    <Fade in={showContent} timeout={2000}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                        {/* First row - 3 codes */}
                        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '900px' }}>
                          {diamondTicketCodes.slice(0, 3).map((code, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Zoom in={showContent} timeout={2200 + (index * 200)}>
                                <Box sx={{ position: 'relative' }}>
                                  <TransparentTextField
                                    fullWidth
                                    placeholder={`💎 CODE ${index + 1} 💎`}
                                    value={code}
                                    onChange={(e) => handleDiamondTicketChange(index, e.target.value)}
                                    error={!!diamondTicketErrors[index]}
                                    helperText={diamondTicketErrors[index]}
                                    sx={{ 
                                      '& .MuiOutlinedInput-root': {
                                        height: isMobile ? '44px' : '60px',
                                        background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)) padding-box, 
                                                    linear-gradient(45deg, ${
                                                      diamondCodeValidation[index] === 'valid' ? '#00FF00, #40E0D0' :
                                                      diamondCodeValidation[index] === 'invalid' ? '#FF1744, #FF6B35' :
                                                      '#40E0D0, #8A2BE2, #FF1744'
                                                    }) border-box`
                                      }
                                    }}
                                    inputProps={{
                                      maxLength: 50,
                                      style: { textAlign: 'center' }
                                    }}
                                    InputProps={{
                                      endAdornment: validatingCode[index] ? (
                                        <CircularProgress 
                                          size={20} 
                                          sx={{ 
                                            color: '#40E0D0',
                                            marginRight: 1
                                          }} 
                                        />
                                      ) : diamondCodeValidation[index] === 'valid' ? (
                                        <Box sx={{ 
                                          color: '#00FF00', 
                                          fontSize: '1.5rem',
                                          marginRight: 1,
                                          animation: 'bounce 0.5s ease-in-out'
                                        }}>
                                          ✓
                                        </Box>
                                      ) : diamondCodeValidation[index] === 'invalid' ? (
                                        <Box sx={{ 
                                          color: '#FF1744', 
                                          fontSize: '1.5rem',
                                          marginRight: 1,
                                          animation: 'shake 0.5s ease-in-out'
                                        }}>
                                          ✗
                                        </Box>
                                      ) : null
                                    }}
                                  />
                                  {/* Dynamic border glow based on validation */}
                                  {diamondCodeValidation[index] === 'valid' && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: '-3px',
                                      left: '-3px',
                                      right: '-3px',
                                      bottom: '-3px',
                                      background: 'linear-gradient(45deg, #00FF00, #40E0D0)',
                                      borderRadius: '23px',
                                      zIndex: -1,
                                      animation: 'validPulse 2s infinite',
                                      '@keyframes validPulse': {
                                        '0%': { 
                                          opacity: 0.8, 
                                          transform: 'scale(1)',
                                          boxShadow: '0 0 20px rgba(0,255,0,0.5)'
                                        },
                                        '50%': { 
                                          opacity: 1, 
                                          transform: 'scale(1.02)',
                                          boxShadow: '0 0 30px rgba(0,255,0,0.8)'
                                        },
                                        '100%': { 
                                          opacity: 0.8, 
                                          transform: 'scale(1)',
                                          boxShadow: '0 0 20px rgba(0,255,0,0.5)'
                                        },
                                      },
                                      '@keyframes bounce': {
                                        '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                                        '40%': { transform: 'translateY(-10px)' },
                                        '60%': { transform: 'translateY(-5px)' },
                                      },
                                      '@keyframes shake': {
                                        '0%, 100%': { transform: 'translateX(0)' },
                                        '25%': { transform: 'translateX(-5px)' },
                                        '75%': { transform: 'translateX(5px)' },
                                      }
                                    }} />
                                  )}
                                  {diamondCodeValidation[index] === 'invalid' && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: '-3px',
                                      left: '-3px',
                                      right: '-3px',
                                      bottom: '-3px',
                                      background: 'linear-gradient(45deg, #FF1744, #FF6B35)',
                                      borderRadius: '23px',
                                      zIndex: -1,
                                      animation: 'invalidShake 1s ease-in-out',
                                      '@keyframes invalidShake': {
                                        '0%': { 
                                          opacity: 0.8, 
                                          transform: 'translateX(0)',
                                          boxShadow: '0 0 20px rgba(255,23,68,0.5)'
                                        },
                                        '25%': { 
                                          opacity: 1, 
                                          transform: 'translateX(-8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '50%': { 
                                          opacity: 1, 
                                          transform: 'translateX(8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '75%': { 
                                          opacity: 1, 
                                          transform: 'translateX(-8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '100%': { 
                                          opacity: 0.8, 
                                          transform: 'translateX(0)',
                                          boxShadow: '0 0 20px rgba(255,23,68,0.5)'
                                        },
                                      }
                                    }} />
                                  )}
                                </Box>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Second row - 2 codes */}
                        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '600px' }}>
                          {diamondTicketCodes.slice(3, 5).map((code, index) => (
                            <Grid item xs={12} sm={6} key={index + 3}>
                              <Zoom in={showContent} timeout={2200 + ((index + 3) * 200)}>
                                <Box sx={{ position: 'relative' }}>
                                  <TransparentTextField
                                    fullWidth
                                    placeholder={`💎 CODE ${index + 4} 💎`}
                                    value={code}
                                    onChange={(e) => handleDiamondTicketChange(index + 3, e.target.value)}
                                    error={!!diamondTicketErrors[index + 3]}
                                    helperText={diamondTicketErrors[index + 3]}
                                    sx={{ 
                                      '& .MuiOutlinedInput-root': {
                                        height: isMobile ? '44px' : '60px',
                                        background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)) padding-box, 
                                                    linear-gradient(45deg, ${
                                                      diamondCodeValidation[index + 3] === 'valid' ? '#00FF00, #40E0D0' :
                                                      diamondCodeValidation[index + 3] === 'invalid' ? '#FF1744, #FF6B35' :
                                                      '#40E0D0, #8A2BE2, #FF1744'
                                                    }) border-box`
                                      }
                                    }}
                                    inputProps={{
                                      maxLength: 50,
                                      style: { textAlign: 'center' }
                                    }}
                                    InputProps={{
                                      endAdornment: validatingCode[index + 3] ? (
                                        <CircularProgress 
                                          size={20} 
                                          sx={{ 
                                            color: '#40E0D0',
                                            marginRight: 1
                                          }} 
                                        />
                                      ) : diamondCodeValidation[index + 3] === 'valid' ? (
                                        <Box sx={{ 
                                          color: '#00FF00', 
                                          fontSize: '1.5rem',
                                          marginRight: 1,
                                          animation: 'bounce 0.5s ease-in-out'
                                        }}>
                                          ✓
                                        </Box>
                                      ) : diamondCodeValidation[index + 3] === 'invalid' ? (
                                        <Box sx={{ 
                                          color: '#FF1744', 
                                          fontSize: '1.5rem',
                                          marginRight: 1,
                                          animation: 'shake 0.5s ease-in-out'
                                        }}>
                                          ✗
                                        </Box>
                                      ) : null
                                    }}
                                  />
                                  {/* Dynamic border glow based on validation */}
                                  {diamondCodeValidation[index + 3] === 'valid' && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: '-3px',
                                      left: '-3px',
                                      right: '-3px',
                                      bottom: '-3px',
                                      background: 'linear-gradient(45deg, #00FF00, #40E0D0)',
                                      borderRadius: '23px',
                                      zIndex: -1,
                                      animation: 'validPulse 2s infinite',
                                      '@keyframes validPulse': {
                                        '0%': { 
                                          opacity: 0.8, 
                                          transform: 'scale(1)',
                                          boxShadow: '0 0 20px rgba(0,255,0,0.5)'
                                        },
                                        '50%': { 
                                          opacity: 1, 
                                          transform: 'scale(1.02)',
                                          boxShadow: '0 0 30px rgba(0,255,0,0.8)'
                                        },
                                        '100%': { 
                                          opacity: 0.8, 
                                          transform: 'scale(1)',
                                          boxShadow: '0 0 20px rgba(0,255,0,0.5)'
                                        },
                                      }
                                    }} />
                                  )}
                                  {diamondCodeValidation[index + 3] === 'invalid' && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: '-3px',
                                      left: '-3px',
                                      right: '-3px',
                                      bottom: '-3px',
                                      background: 'linear-gradient(45deg, #FF1744, #FF6B35)',
                                      borderRadius: '23px',
                                      zIndex: -1,
                                      animation: 'invalidShake 1s ease-in-out',
                                      '@keyframes invalidShake': {
                                        '0%': { 
                                          opacity: 0.8, 
                                          transform: 'translateX(0)',
                                          boxShadow: '0 0 20px rgba(255,23,68,0.5)'
                                        },
                                        '25%': { 
                                          opacity: 1, 
                                          transform: 'translateX(-8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '50%': { 
                                          opacity: 1, 
                                          transform: 'translateX(8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '75%': { 
                                          opacity: 1, 
                                          transform: 'translateX(-8px)',
                                          boxShadow: '0 0 25px rgba(255,23,68,0.7)'
                                        },
                                        '100%': { 
                                          opacity: 0.8, 
                                          transform: 'translateX(0)',
                                          boxShadow: '0 0 20px rgba(255,23,68,0.5)'
                                        },
                                      }
                                    }} />
                                  )}
                                </Box>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Fade>
                    
                    <Zoom in={showContent} timeout={3000}>
                      <HuntButton 
                        variant="contained" 
                        size={isMobile ? "large" : "large"}
                        onClick={handleDiamondTicketSubmit}
                        disabled={
                          diamondTicketErrors.some(error => error) || 
                          diamondTicketCodes.some(code => !code) ||
                          diamondTicketLoading ||
                          validatingCode.some(validating => validating) ||
                          diamondCodeValidation.some(validation => validation === 'invalid') ||
                          !diamondCodeValidation.every(validation => validation === 'valid')
                        }
                        sx={{
                          background: "linear-gradient(45deg, #40E0D0 0%, #8A2BE2 50%, #FF1744 100%)",
                          fontSize: isMobile ? "1.1rem" : "1.3rem",
                          padding: isMobile ? "12px 30px" : "16px 50px",
                          position: 'relative',
                          '&:hover': {
                            background: "linear-gradient(45deg, #FF1744 0%, #40E0D0 50%, #8A2BE2 100%)",
                          }
                        }}
                      >
                        {diamondTicketLoading ? (
                          <>
                            <CircularProgress 
                              size={24} 
                              sx={{ 
                                color: 'white', 
                                marginRight: 1,
                                position: 'absolute',
                                left: '20px'
                              }} 
                            />
                            <Box sx={{ ml: 4 }}>PROCESSING...</Box>
                          </>
                        ) : (
                          '💎 CLAIM DIAMOND EMPIRE 💎'
                        )}
                      </HuntButton>
                    </Zoom>
                  </Box>
                </HuntSection>
              </Zoom>
            </Grid>
          </Grid>
        </Fade>
      </ContentWrapper>

      {/* Test Modal Button - Remove after testing */}
      {/* <Box sx={{ 
        position: 'fixed', 
        top: 20, 
        right: 20, 
        zIndex: 9999 
      }}>
        <Button
          variant="contained"
          onClick={() => setWinModal({
            open: true,
            ticketType: 'golden',
            ticketCode: 'TEST123CODE',
            winMessage: 'Test modal with black background and ring effect! 🎉',
            prizeAmount: 1000,
            hasWin: true
          })}
          sx={{
            background: "linear-gradient(45deg, #FFD700 0%, #FF6B35 50%, #8A2BE2 100%)",
            color: "white",
            fontWeight: "bold",
            padding: "10px 20px",
            borderRadius: "20px",
            boxShadow: "0 4px 15px rgba(255, 215, 0, 0.4)"
          }}
        >
          🎯 Test Modal
        </Button>
      </Box> */}

      {/* Win Modal */}
      <WinModal
        open={winModal.open}
        onClose={handleCloseWinModal}
        ticketType={winModal.ticketType}
        ticketCode={winModal.ticketCode}
        winMessage={winModal.winMessage}
        prizeAmount={winModal.prizeAmount}
        hasWin={winModal.hasWin}
        onScreenshot={takeScreenshot}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: '100%',
            fontSize: '1.1rem',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </HuntEventContainer>
  );
};

export default HuntEvent;
